import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import React,{ useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion,AnimatePresence } from 'framer-motion'
import { ArrowRight,Building2,Lock,Mail,Edit2,Loader2,ArrowLeft,CheckCircle2 } from 'lucide-react'
import { createFileRoute,useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card,CardContent,CardHeader,CardTitle } from '@/components/ui/card'
import { api } from '@/lib/api-client'
import { authManager,requireGuest } from '@/lib/auth'
import { clearSelectedStore } from '@/features/shared'
import { queryKeys } from '@/lib/query-keys'
import type { OrganizationSlugLookup } from '@/types'

export const Route = createFileRoute('/')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: LoginPage,
  beforeLoad: requireGuest('/dashboard'),
})

function LoginPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [isCheckingOrg, setIsCheckingOrg] = useState(false)
  const [orgError, setOrgError] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(null)
  const [orgLogo, setOrgLogo] = useState<string | null>(null)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotError, setForgotError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    organization: '',
    email: '',
    password: '',
  })

  // Skip the org-name step when a slug is remembered from a previous sign-in
  // (survives logout). Resolve it to confirm it still exists + fetch the logo;
  // an unknown/deleted org silently falls back to step 1.
  const [rememberedSlug] = useState(() => authManager.getLastOrgSlug())
  const rememberedOrgQuery = useQuery({
    queryKey: queryKeys.orgBySlug(rememberedSlug ?? ''),
    queryFn: () => api.get<OrganizationSlugLookup>(`/organizations/slug/${rememberedSlug}`),
    enabled: !!rememberedSlug,
    retry: false,
    staleTime: 5 * 60_000,
  })
  // Adjust-during-render (no setState-in-effect): once the remembered org
  // resolves, prefill it and advance to the sign-in step exactly once.
  const [orgPrefilled, setOrgPrefilled] = useState(false)
  if (!orgPrefilled && rememberedSlug && rememberedOrgQuery.data?.id && step === 1) {
    setOrgPrefilled(true)
    setResolvedOrgId(rememberedOrgQuery.data.id)
    if (rememberedOrgQuery.data.avatarUrl) setOrgLogo(rememberedOrgQuery.data.avatarUrl)
    setFormData((prev) => ({ ...prev, organization: rememberedSlug }))
    setStep(2)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    if (e.target.name === 'organization') setOrgError(null)
    if (e.target.name === 'email' || e.target.name === 'password') setLoginError(null)
  }

  const handleNextStep = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.organization.trim()) return

    setIsCheckingOrg(true)
    setOrgError(null)

    try {
      const response = await api.get<{ id?: string; displayName?: string; slug?: string; avatarUrl?: string }>(`/organizations/slug/${formData.organization}`
      )
      const data = response

      if (data && data.id) {
        setResolvedOrgId(data.id)
        if (data.avatarUrl) setOrgLogo(data.avatarUrl)
        setStep(2)
      } else {
        setOrgError('Organization not found')
      }
    } catch {
      setOrgError('Organization not found')
    } finally {
      setIsCheckingOrg(false)
    }
  }

  const handleEditOrg = () => {
    setStep(1)
    setResolvedOrgId(null)
    setOrgLogo(null)
    setFormData((prev) => ({ ...prev, password: '' }))
    setOrgError(null)
    setLoginError(null)
    setForgotEmail('')
    setForgotError(null)
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotLoading(true)
    setForgotError(null)
    try {
      await api.post(`/auth/forgot-password`,
        { email: forgotEmail || formData.email, organizationId: resolvedOrgId },
        { headers: { 'x-skip-auth-redirect': '1' } }
      )
      setStep(4)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      setForgotError(err.response?.data?.message || 'Something went wrong. Please try again.')
    } finally {
      setForgotLoading(false)
    }
  }

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setLoginError(null)

    try {
      const payload = {
        organizationId: resolvedOrgId || formData.organization,
        email: formData.email,
        password: formData.password,
      }

      const response = await api.post(`/auth/login`, payload)
      const data = response as Record<string, unknown>

      authManager.login(data, resolvedOrgId || undefined)
      // Remember the org slug so the next login (after logout) skips step 1.
      authManager.setLastOrgSlug(formData.organization)
      // Force a fresh store choice on every login: clear any previously
      // persisted selection so the dashboard shows the store-selection modal
      // (multi-store) instead of silently reusing the last-used store. Mirrors
      // the /auth/:slug login path.
      clearSelectedStore()

      navigate({ to: '/dashboard' })
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } } }
      setLoginError(err.response?.data?.message || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  // Suppress the org-name step's flash while a remembered slug is resolving —
  // show the standard route spinner until we know whether to jump to step 2.
  if (rememberedSlug && !orgPrefilled && rememberedOrgQuery.isLoading) {
    return <RoutePending />
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl">
          <CardHeader className="space-y-4 text-center">
            <motion.div layout className="mx-auto w-16 h-16 flex items-center justify-center overflow-hidden">
              <AnimatePresence mode="wait">
                {orgLogo ? (
                  <motion.img
                    key="org-logo"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    src={orgLogo}
                    alt="Organization Logo"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                ) : (
                  <motion.img
                    key="default-logo"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    src="https://cdn.rootpoint.io/platform/logo-color.svg"
                    alt="RootPoint Logo"
                    className="w-full h-full object-contain"
                  />
                )}
              </AnimatePresence>
            </motion.div>
            <motion.div layout>
              <CardTitle className="text-3xl font-bold text-balance">RootPoint</CardTitle>
              <motion.p layout className="text-sm text-muted-foreground mt-2">
                {step === 1
                  ? 'Enter your organization name to continue'
                  : step === 3
                    ? 'Reset your password'
                    : step === 4
                      ? 'Check your email'
                      : 'Sign in to your account'}
              </motion.p>
            </motion.div>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={step === 1 ? handleNextStep : step === 3 ? handleForgotPassword : handleFinalSubmit}
              className="space-y-6"
            >
              {(step === 1 || step === 2) && (
                <motion.div layout className="relative space-y-2">
                  <motion.div layout className="flex justify-between items-center">
                    <Label htmlFor="organization">Organization Name</Label>
                    {step === 2 && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        type="button"
                        onClick={handleEditOrg}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Edit2 className="h-3 w-3" /> Change
                      </motion.button>
                    )}
                  </motion.div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building2
                        className={`h-4 w-4 transition-colors ${step === 2 ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                    </div>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      disabled={step === 2 || isCheckingOrg}
                      className={`pl-10 h-11 transition-all focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary ${step === 2 ? 'bg-muted/50' : ''} ${orgError ? 'border-destructive focus-visible:border-destructive' : ''}`}
                      placeholder="e.g. acme-corp"
                      autoFocus={step === 1}
                    />
                  </div>
                  {orgError && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-destructive font-medium"
                    >
                      {orgError}
                    </motion.p>
                  )}
                </motion.div>
              )}

              <AnimatePresence mode="popLayout">
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20, height: 0 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      height: 'auto',
                      transition: {
                        height: { duration: 0.3 },
                        opacity: { duration: 0.4, delay: 0.1 },
                      },
                    }}
                    exit={{ opacity: 0, y: -20, height: 0 }}
                    className="space-y-4 overflow-hidden px-1 py-1 -mx-1"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Input
                          id="email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                          placeholder="name@company.com"
                          autoFocus={step === 2}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          className="text-sm text-primary hover:underline"
                          onClick={() => {
                            setForgotEmail(formData.email)
                            setForgotError(null)
                            setStep(3)
                          }}
                        >
                          Forgot?
                        </button>
                      </div>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Input
                          id="password"
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="popLayout">
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20, height: 0 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      height: 'auto',
                      transition: { height: { duration: 0.3 }, opacity: { duration: 0.4, delay: 0.1 } },
                    }}
                    exit={{ opacity: 0, y: -20, height: 0 }}
                    className="space-y-4 overflow-hidden px-1 py-1 -mx-1"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email Address</Label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <Input
                          id="forgot-email"
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => {
                            setForgotEmail(e.target.value)
                            setForgotError(null)
                          }}
                          className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                          placeholder="name@company.com"
                          autoFocus
                        />
                      </div>
                    </div>
                    {forgotError && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-destructive font-medium"
                      >
                        {forgotError}
                      </motion.p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="popLayout">
                {step === 4 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center gap-3 py-4 text-center"
                  >
                    <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                    <p className="font-medium">Reset link sent!</p>
                    <p className="text-sm text-muted-foreground">
                      Check your inbox for a password reset link. It may take a few minutes.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div layout>
                {step === 4 ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11"
                    onClick={() => {
                      setStep(2)
                      setForgotEmail('')
                      setForgotError(null)
                    }}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Sign In
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button
                      type="submit"
                      className="w-full h-11 transition-all"
                      disabled={loading || isCheckingOrg || forgotLoading}
                    >
                      {loading || isCheckingOrg || forgotLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {step === 1 ? 'Checking...' : step === 3 ? 'Sending...' : 'Signing in...'}
                        </>
                      ) : (
                        <span className="flex items-center gap-2">
                          {step === 1 ? (
                            <>
                              Continue <ArrowRight className="h-4 w-4" />
                            </>
                          ) : step === 3 ? (
                            'Send Reset Link'
                          ) : (
                            'Sign In'
                          )}
                        </span>
                      )}
                    </Button>
                    {step === 3 && (
                      <button
                        type="button"
                        className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center pt-1"
                        onClick={() => {
                          setStep(2)
                          setForgotEmail('')
                          setForgotError(null)
                        }}
                      >
                        <span className="flex items-center justify-center gap-1">
                          <ArrowLeft className="h-3 w-3" /> Back to Sign In
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </motion.div>

              {loginError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive font-medium text-center"
                >
                  {loginError}
                </motion.p>
              )}
            </form>
          </CardContent>
        </Card>
      </motion.div>
      <div className="mt-8 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} RootPoint. All rights reserved.
      </div>
    </div>
  )
}
